import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Globe, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import theme from '../../styles/theme';
import BrandLogo from '../common/BrandLogo';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

const PublicNavbar = ({ onLoginClick }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const langRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[1];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: t('public.nav.features', 'Features'), to: '/features' },
    { label: t('public.footer.pricing', 'Pricing'), to: '/pricing' },
    { label: t('public.nav.contact', 'Contact'), to: '/contact' },
  ];

  const s = {
    nav: {
      position: 'sticky',
      top: 0,
      zIndex: 100,
      backgroundColor: '#FFFFFF',
      borderBottom: scrolled ? `1px solid ${theme.colors.border.light}` : '1px solid transparent',
      boxShadow: scrolled ? theme.shadows.md : 'none',
      transition: 'box-shadow 200ms ease, border-color 200ms ease',
    },
    inner: {
      maxWidth: '1120px',
      margin: '0 auto',
      padding: `0 ${theme.spacing.xl}`,
      height: '64px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    links: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.xl,
    },
    link: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.text.secondary,
      textDecoration: 'none',
      transition: `color ${theme.transitions.fast}`,
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.md,
    },
    loginBtn: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.text.primary,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    },
    langWrapper: {
      position: 'relative',
    },
    langBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.text.secondary,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
      borderRadius: theme.borderRadius.md,
      minHeight: '36px',
    },
    langDropdown: {
      position: 'absolute',
      top: '100%',
      right: 0,
      marginTop: '6px',
      backgroundColor: theme.colors.background.paper,
      borderRadius: theme.borderRadius.md,
      boxShadow: theme.shadows.lg,
      overflow: 'hidden',
      minWidth: '120px',
      zIndex: 1000,
    },
    langOption: (isActive) => ({
      display: 'block',
      width: '100%',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      border: 'none',
      background: isActive ? theme.colors.primary.light : 'transparent',
      color: isActive ? theme.colors.primary.main : theme.colors.text.primary,
      fontWeight: isActive ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal,
      fontSize: theme.typography.fontSize.sm,
      textAlign: 'left',
      cursor: 'pointer',
      fontFamily: theme.typography.fontFamily.primary,
    }),
    ctaBtn: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.accent.onMain,
      backgroundColor: theme.colors.accent.main,
      border: 'none',
      borderRadius: theme.borderRadius.full,
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      cursor: 'pointer',
      transition: `background-color ${theme.transitions.fast}`,
      textDecoration: 'none',
    },
    mobileMenuBtn: {
      display: 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: theme.colors.text.primary,
      padding: theme.spacing.sm,
    },
    mobileMenu: {
      position: 'absolute',
      top: '64px',
      left: 0,
      right: 0,
      backgroundColor: '#fff',
      borderBottom: `1px solid ${theme.colors.border.light}`,
      boxShadow: theme.shadows.md,
      padding: theme.spacing.lg,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.md,
      zIndex: 99,
    },
    mobileLink: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.text.primary,
      textDecoration: 'none',
      padding: `${theme.spacing.sm} 0`,
      borderBottom: `1px solid ${theme.colors.border.light}`,
    },
  };

  return (
    <nav style={s.nav} ref={menuRef}>
      <div style={s.inner}>
        {/* Logo */}
        <Link to="/" style={s.logo}>
          <BrandLogo
            label="Waggy"
            size={32}
            textSize={theme.typography.fontSize.xl}
            markColor={theme.colors.primary.main}
            symbolColor="#FFFFFF"
            accentColor={theme.colors.accent.main}
            textColor={theme.colors.text.primary}
          />
        </Link>

        {/* Desktop links */}
        <div style={s.links} className="public-nav-links">
          {navLinks.map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              style={s.link}
              onMouseOver={(e) => (e.target.style.color = theme.colors.primary.main)}
              onMouseOut={(e) => (e.target.style.color = theme.colors.text.secondary)}
            >
              {label}
            </Link>
          ))}
        </div>

        {/* Desktop actions */}
        <div style={s.actions} className="public-nav-actions">
          <div style={s.langWrapper} ref={langRef}>
            <button
              style={s.langBtn}
              onClick={() => setLangOpen((o) => !o)}
              aria-label={t('public.nav.changeLanguage', 'Change language')}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = theme.colors.background.elevated)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Globe size={15} />
              {currentLang.label}
            </button>
            {langOpen && (
              <div style={s.langDropdown}>
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    style={s.langOption(lang.code === i18n.language)}
                    onClick={() => changeLanguage(lang.code)}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            style={s.loginBtn}
            onClick={onLoginClick}
            onMouseOver={(e) => (e.currentTarget.style.color = theme.colors.primary.main)}
            onMouseOut={(e) => (e.currentTarget.style.color = theme.colors.text.primary)}
          >
            {t('public.nav.login', 'Log in')}
          </button>
          <Link
            to="/register/start?plan=growth"
            style={s.ctaBtn}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = theme.colors.accent.hoverAccessible)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = theme.colors.accent.main)}
          >
            {t('public.nav.requestDemo', 'Start demo')}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          style={{ ...s.mobileMenuBtn, display: 'flex' }}
          className="public-nav-mobile-btn"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div style={s.mobileMenu}>
          {navLinks.map(({ label, to }) => (
            <Link key={to} to={to} style={s.mobileLink} onClick={() => setMenuOpen(false)}>
              {label}
            </Link>
          ))}
          <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                style={{
                  ...s.langOption(lang.code === i18n.language),
                  border: `1px solid ${lang.code === i18n.language ? theme.colors.primary.main : theme.colors.border.light}`,
                  borderRadius: theme.borderRadius.full,
                  width: 'auto',
                }}
                onClick={() => changeLanguage(lang.code)}
              >
                {lang.label}
              </button>
            ))}
          </div>
          <button
            style={{ ...s.loginBtn, textAlign: 'left', padding: `${theme.spacing.sm} 0` }}
            onClick={() => { setMenuOpen(false); onLoginClick(); }}
          >
            {t('public.nav.login', 'Log in')}
          </button>
          <Link
            to="/register/start?plan=growth"
            style={{ ...s.ctaBtn, textAlign: 'center', display: 'block' }}
            onClick={() => setMenuOpen(false)}
          >
            {t('public.nav.requestDemo', 'Start demo')}
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .public-nav-links, .public-nav-actions { display: none !important; }
          .public-nav-mobile-btn { display: flex !important; }
        }
        @media (min-width: 769px) {
          .public-nav-mobile-btn { display: none !important; }
        }
      `}</style>
    </nav>
  );
};

export default PublicNavbar;
